import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, ShoppingBag, Landmark } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import api, { eur } from "../lib/api";

const LABELS = {
  PAID: { icon: CheckCircle2, color: "#15803D", title: "Pagamento riuscito", text: "Grazie! Il tuo ordine è confermato. Riceverai una email con i dettagli." },
  AWAITING_TRANSFER: { icon: Landmark, color: "#854D0E", title: "Ordine registrato: attendiamo il bonifico", text: "Ti abbiamo inviato via email IBAN, importo e causale. Appena riceviamo il bonifico confermiamo l'ordine e ti inviamo la ricevuta." },
  PENDING: { icon: Clock, color: "#B45309", title: "Ordine registrato", text: "Stiamo verificando il pagamento oppure sarai ricontattato per completarlo." },
  PROCESSING: { icon: Clock, color: "#B45309", title: "Pagamento in corso", text: "Attendere la conferma del pagamento…" },
  FAILED: { icon: XCircle, color: "#B91C1C", title: "Pagamento non riuscito", text: "Il pagamento non è andato a buon fine. Riprova o contattaci." },
  CANCELLED: { icon: XCircle, color: "#B91C1C", title: "Pagamento annullato", text: "Hai annullato il pagamento. Il tuo carrello è ancora disponibile." },
  ERROR: { icon: XCircle, color: "#B91C1C", title: "Errore", text: "Si è verificato un errore. Contattaci per assistenza." },
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("PENDING");

  useEffect(() => {
    if (!orderId) return;
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/shop/orders/${orderId}`);
        if (stopped) return;
        setOrder(data); setStatus(data.status);
        if (["PENDING", "PROCESSING"].includes(data.status) && tries < 8) {
          tries += 1;
          setTimeout(poll, 2500);
        }
      } catch (err) { console.error("Stato ordine non disponibile, nuovo tentativo al prossimo poll", err); }
    };
    poll();
    return () => { stopped = true; };
  }, [orderId]);

  const meta = LABELS[status] || LABELS.PENDING;
  const Icon = meta.icon;

  return (
    <div className="paper-grain min-h-screen">
      <Navbar />
      <section className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-result">
        <Icon className="w-16 h-16 mx-auto mb-6" style={{ color: meta.color }} />
        <h1 className="font-serif font-semibold text-3xl lg:text-4xl text-[#0B2A45]" data-testid="payment-status-title">{meta.title}</h1>
        <p className="text-stone-600 mt-4">{meta.text}</p>
        {orderId && <p className="text-xs text-stone-400 mt-3 font-mono">Rif. ordine: {orderId}</p>}
        {status === "AWAITING_TRANSFER" && order?.bank && (
          <div className="mt-6 bg-[#FEF9C3] border border-[#FDE68A] rounded-2xl p-5 text-left max-w-md mx-auto text-sm text-[#0B2A45]" data-testid="bank-transfer-details">
            <div className="font-semibold mb-2">Dati per il bonifico</div>
            <div><span className="text-stone-500">Intestatario:</span> <b>{order.bank.holder}</b></div>
            <div><span className="text-stone-500">IBAN:</span> <b className="font-mono">{order.bank.iban}</b></div>
            <div><span className="text-stone-500">Importo:</span> <b>{eur(order.total)}</b></div>
            <div><span className="text-stone-500">Causale:</span> <b>Ordine {orderId}</b></div>
          </div>
        )}
        {order && order.total != null && (
          <div className="mt-8 bg-white rounded-2xl border border-stone-200 p-6 text-left max-w-md mx-auto">
            {(order.items || []).map((it, i) => (
              <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between text-sm py-1">
                <span className="text-stone-600">{it.quantity}× {it.name}</span>
                <span className="font-mono">{eur(it.line_total)}</span>
              </div>
            ))}
            <div className="border-t mt-2 pt-2 flex justify-between text-sm"><span className="text-stone-500">Spedizione</span><span className="font-mono">{order.shipping === 0 ? "Gratis" : eur(order.shipping)}</span></div>
            <div className="flex justify-between font-semibold pt-1"><span>Totale</span><span className="font-mono text-[#0F4C81]">{eur(order.total)}</span></div>
          </div>
        )}
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/negozio"><Button variant="outline" className="rounded-full" data-testid="back-to-shop-btn"><ShoppingBag className="w-4 h-4 mr-2" />Torna al negozio</Button></Link>
          <Link to="/"><Button className="bg-[#0F4C81] text-white rounded-full">Home</Button></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
